import type {
  Brief,
  BriefSummary,
  Capability,
  CaseStudy,
  GenerateBriefRequest,
  Me,
  MeetingPrep,
  OrganisationPage,
  Sector,
  StageId,
} from '../types'

/** Thrown on 401 — the session expired or was never established. */
export class UnauthorizedError extends Error {
  constructor() {
    super('Not signed in')
  }
}

/** Carries the backend's ProblemDetail `detail`, which is written to be shown to the user. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

/**
 * Spring writes the CSRF token to a JS-readable XSRF-TOKEN cookie; we echo it back in a
 * header on every mutating request. The session cookie itself stays HttpOnly and is never
 * touched from here — that is the whole point of the BFF pattern.
 */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = csrfToken()
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { 'X-XSRF-TOKEN': token } : {}),
      ...init.headers,
    },
  })

  if (res.status === 401) throw new UnauthorizedError()

  if (!res.ok) {
    // The backend returns RFC 7807 ProblemDetail; `detail` is the user-facing message.
    const problem = await res.json().catch(() => null)
    throw new ApiError(problem?.detail ?? `Request failed (${res.status})`, res.status)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  me: () => request<Me>('/api/auth/me'),

  sectors: () => request<Sector[]>('/api/catalog/sectors'),

  organisations: (sectorId: string, filters: { segment?: string; tier?: string; q?: string } = {}) => {
    const params = new URLSearchParams()
    if (filters.segment) params.set('segment', filters.segment)
    if (filters.tier) params.set('tier', filters.tier)
    if (filters.q) params.set('q', filters.q)
    const qs = params.toString()
    return request<OrganisationPage>(
      `/api/catalog/sectors/${encodeURIComponent(sectorId)}/organisations${qs ? `?${qs}` : ''}`,
    )
  },

  caseStudies: () => request<CaseStudy[]>('/api/catalog/case-studies'),

  capabilities: () => request<Capability[]>('/api/catalog/capabilities'),

  briefs: () => request<BriefSummary[]>('/api/briefs'),

  brief: (company: string) => request<Brief>(`/api/briefs/${encodeURIComponent(company)}`),

  generateBrief: (body: GenerateBriefRequest) =>
    request<Brief>('/api/briefs', { method: 'POST', body: JSON.stringify(body) }),

  setStage: (company: string, stage: StageId) =>
    request<Brief>(`/api/briefs/${encodeURIComponent(company)}/stage`, {
      method: 'PUT',
      body: JSON.stringify({ stage }),
    }),

  saveMeetingPrep: (company: string, prep: MeetingPrep) =>
    request<Brief>(`/api/briefs/${encodeURIComponent(company)}/meeting-prep`, {
      method: 'PUT',
      body: JSON.stringify(prep),
    }),

  generateFollowup: (company: string, notes: string) =>
    request<Brief>(`/api/briefs/${encodeURIComponent(company)}/followup`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),

  deleteBrief: (company: string) =>
    request<void>(`/api/briefs/${encodeURIComponent(company)}`, { method: 'DELETE' }),
}
